import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { CreditCardIcon, UploadIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useRef, useState } from 'react'

import { API } from '../../api/apis.config'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import StaffCoordinatesFilter from '../../components/pages/staff-coordinates/staff-coordinates-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import {
  FieldStatusEnum,
  fieldStatusEnumMapping
} from '../../lib/types/enums/affected-staff/field-status.enum'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { StaffCoordinates, StaffCoordinatesFilters } from '../../lib/types/models/staff/staff'
import {
  transformStaffCoordinatesFiltersToUrl,
  transformUrlToStaffCoordinatesFilters
} from '../../lib/utils/filters-mapping/staff-coordinates'

const StaffBankDetailsListPage = () => {
  const tableRef = useRef<DynamicTableRef<StaffCoordinates>>(null)
  const { navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])
  const { t } = useTranslate()

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<StaffCoordinatesFilters>({
      initialFilters: { ibanExported: false },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformStaffCoordinatesFiltersToUrl,
      transformUrlToFilter: transformUrlToStaffCoordinatesFilters
    })

  const columns: Column<StaffCoordinates>[] = [
    {
      accessorKey: 'id',
      header: '#'
    },
    {
      enableSorting: true,
      accessorKey: 'lastName',
      header: 'Nom'
    },
    {
      enableSorting: true,
      accessorKey: 'firstName',
      header: 'Prénom'
    },
    {
      enableSorting: true,
      accessorKey: 'position',
      header: 'Poste',
      cell: ({ row }) => t(staffPositionEnumMapping[row.original.position])
    },
    {
      enableSorting: true,
      accessorKey: 'ibanStatus',
      header: 'Statut',
      cell: ({ row }) => {
        const status = row.original.ibanStatus
        const date = row.original.ibanUpdatedAt
        if (!status || status === FieldStatusEnum.EMPTY) return '-'
        return (
          <div className="flex flex-col gap-1 py-2">
            <Badge
              text={fieldStatusEnumMapping[status].label}
              variant={fieldStatusEnumMapping[status].variant}
            />
            <Badge text={date ? DateFormatter.DDMMYYYY(date) : '-'} variant="grey" />
          </div>
        )
      }
    },
    {
      enableSorting: true,
      accessorKey: 'ibanExportedAt',
      header: 'Exporté',
      cell: ({ row }) => {
        const date = row.original.ibanExportedAt
        if (date)
          return (
            <Badge
              text={
                <div className="flex gap-1 items-center">
                  <Typography variant="label-semibold" color={tailwindColors.green.DEFAULT}>
                    Oui
                  </Typography>{' '}
                  -{' '}
                  <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
                    {DateFormatter.DDMMYYYY(date)}
                  </Typography>
                </div>
              }
              variant="grey"
            />
          )
        return (
          <Badge
            text="Non"
            borderColor={tailwindColors.grey.light.DEFAULT}
            color={tailwindColors.white.active}
            textColor={tailwindColors.red.DEFAULT}
          />
        )
      }
    }
  ]

  return (
    <div>
      <UserNavBar title={'Nouvelles coordonnées bancaire'} icon={CreditCardIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<StaffCoordinates, StaffCoordinatesFilters>
        ref={tableRef}
        title={'Toutes les nouvelles coordonnées'}
        columns={columns}
        onLoadingChange={setIsTableLoading}
        selectable
        allowSearchParam
        onRowClick={(row, event) =>
          navigateWithEvent(Paths.STAFF_UPDATE_PAGE.replace(':id', row.id.toString()), event)
        }
        version={'FULL'}
        permissions={{ delete: false }}
        endpoint={API.staffCoordinates.getAll.url}
        filters={{
          ...selectedFilter,
          // Only new or changed bank details belong to this list, whatever the status filter
          ibanStatus: selectedFilter?.ibanStatus?.length
            ? selectedFilter.ibanStatus
            : [FieldStatusEnum.NEW, FieldStatusEnum.CHANGED]
        }}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'multiple',
            icon: UploadIcon,
            label: 'Exporter en CSV',
            fn: async (staffs) => {
              const ids = staffs.map((staff) => staff.id)
              await downloadDocuments({
                endpoint: API.staffCoordinates.exportExcel.url,
                ids
              })
              tableRef.current?.refetch()
            }
          }
        ]}
      />

      {/* Filters Modal */}
      <FilterDrawer<StaffCoordinatesFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <StaffCoordinatesFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default StaffBankDetailsListPage
